import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as api from '../services/api';
import { useCart } from '../contexts/CartContext';
import { convertGoogleDriveUrlForDisplay, getPlaceholderImage } from '../utils/imageUtils';
import './ProductDetail.css';

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    loadProduct();
  }, [id]);

  useEffect(() => {
    if (product && product.variants && product.variants.length > 0) {
      setSelectedVariant(product.variants[0]);
    }
  }, [product]);

  const loadProduct = async () => {
    try {
      setLoading(true);
      setError('');
      const productData = await api.getProduct(id);
      setProduct(productData);
    } catch (error) {
      console.error('Error loading product:', error);
      setError('Product not found');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!product) return;
    
    // Validate product ID before attempting to add to cart
    if (!product._id) {
      alert('Error: Product ID is missing. Please refresh the page and try again.');
      return;
    }
    
    if (adding) return;
    
    try {
      setAdding(true);
      const variant = selectedVariant?.sku ? {
        sku: selectedVariant.sku,
        sizeLabel: selectedVariant.label,
        price: selectedVariant.price,
        mrp: selectedVariant.mrp
      } : null;
      
      await addToCart(product._id, variant, quantity);
      alert('Product added to cart successfully!');
    } catch (error) {
      console.error('Error adding to cart:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Failed to add to cart. Please try again.';
      alert(errorMsg);
    } finally {
      setAdding(false);
    }
  };

  const handleQuantityChange = (delta) => {
    const newQuantity = Math.max(1, quantity + delta);
    setQuantity(newQuantity);
  };

  if (loading) {
    return (
      <div className="product-detail-container">
        <div className="product-detail-loading">
          <p>Loading product...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="product-detail-container">
        <div className="product-detail-error">
          <p>{error || 'Product not found'}</p>
          <button onClick={() => navigate('/')} className="back-button">
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  const hasVariants = product.variants && product.variants.length > 1;
  // Only use variant prices when variants exist, otherwise use product prices
  const currentPrice = hasVariants 
    ? (selectedVariant?.price || product.variants?.[0]?.price || 0)
    : (product.price || 0);
  const currentMrp = hasVariants
    ? (selectedVariant?.mrp || product.variants?.[0]?.mrp || currentPrice)
    : (product.mrp || currentPrice);
  const discount = currentMrp > currentPrice ? Math.round(((currentMrp - currentPrice) / currentMrp) * 100) : 0;
  const images = product.images || [];
  const displayImage = images[selectedImageIndex] || images[0] || getPlaceholderImage(600, 600, 'No Image');

  return (
    <div className="product-detail-container">
      <button 
        onClick={() => navigate(-1)} 
        className="back-button"
      >
        ← Back
      </button>

      <div className="product-detail-layout">
        {/* Left: Image Gallery */}
        <div className="product-detail-images">
          <div className="main-image">
            <img 
              src={convertGoogleDriveUrlForDisplay(displayImage)} 
              alt={product.title || product.name}
              onError={(e) => {
                // Prevent infinite retry loops
                if (e.target.dataset.retryCount) {
                  e.target.src = getPlaceholderImage(600, 600, 'No Image');
                  e.target.onerror = null; // Stop retrying
                  return;
                }
                e.target.dataset.retryCount = '1';
                
                const originalSrc = e.target.src;
                // Skip problematic URLs
                if (originalSrc.includes('lh3.googleusercontent.com') || 
                    originalSrc.includes('drive.google.com/uc?export=download')) {
                  e.target.src = getPlaceholderImage(600, 600, 'No Image');
                  e.target.onerror = null;
                  return;
                }
                
                e.target.src = getPlaceholderImage(600, 600, 'No Image');
                e.target.onerror = null; // Stop retrying after first attempt
              }}
            />
            {hasVariants && selectedVariant && selectedVariant.mrp && selectedVariant.mrp > selectedVariant.price && (
              <div className="discount-badge">{discount}% OFF</div>
            )}
            {!hasVariants && discount > 0 && (
              <div className="discount-badge">{discount}% OFF</div>
            )}
          </div>
          {images.length > 1 && (
            <div className="image-thumbnails">
              {images.map((img, index) => (
                <div
                  key={index}
                  className={`thumbnail ${index === selectedImageIndex ? 'active' : ''}`}
                  onClick={() => setSelectedImageIndex(index)}
                >
                  <img 
                    src={convertGoogleDriveUrlForDisplay(img)} 
                    alt={`${product.title || product.name} - View ${index + 1}`}
                    onError={(e) => {
                      // Prevent infinite retry loops
                      e.target.style.display = 'none';
                      e.target.onerror = null; // Stop retrying
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right: Product Info */}
        <div className="product-detail-info">
          <div className="product-breadcrumb">
            {product.category && (
              <span>
                {product.category.name || product.category}
                {product.subcategory && ` > ${product.subcategory}`}
              </span>
            )}
          </div>

          <h1 className="product-title">{product.title || product.name}</h1>

          {product.description && (
            <div className="product-description">
              <p>{product.description}</p>
            </div>
          )}

          {/* Only show price section if no variants exist */}
          {!hasVariants && (
            <div className="product-price-section">
              <div className="price-row">
                <span className="current-price">₹{currentPrice}</span>
                {currentMrp > currentPrice && (
                  <>
                    <span className="original-price">₹{currentMrp}</span>
                    {discount > 0 && (
                      <span className="discount-text">{discount}% off</span>
                    )}
                  </>
                )}
              </div>
              {currentMrp > currentPrice && (
                <p className="savings-text">You save ₹{currentMrp - currentPrice}</p>
              )}
            </div>
          )}

          {hasVariants && (
            <div className="product-variants-section">
              <h3>Select Variant:</h3>
              <div className="variants-grid">
                {product.variants.map((variant, index) => {
                  const isSelected = selectedVariant?.sku === variant.sku || 
                                    (!selectedVariant?.sku && index === 0);
                  const isOutOfStock = variant.stock === 0;
                  return (
                    <button
                      key={variant.sku || index}
                      className={`variant-option ${isSelected ? 'selected' : ''} ${isOutOfStock ? 'out-of-stock' : ''}`}
                      onClick={() => !isOutOfStock && setSelectedVariant(variant)}
                      disabled={isOutOfStock}
                    >
                      <span className="variant-label">{variant.label || variant.sizeLabel}</span>
                      <div className="variant-price-wrapper">
                        <span className="variant-price">₹{variant.price}</span>
                        {variant.mrp && variant.mrp > variant.price && (
                          <span className="variant-mrp">₹{variant.mrp}</span>
                        )}
                      </div>
                      {isOutOfStock && (
                        <span className="out-of-stock-badge">Out of stock</span>
                      )}
                    </button>
                  );
                })}
              </div>
              
              {/* Show selected variant price details */}
              {selectedVariant && (
                <div className="selected-variant-price-section">
                  <div className="price-row">
                    <span className="current-price">₹{selectedVariant.price}</span>
                    {selectedVariant.mrp && selectedVariant.mrp > selectedVariant.price && (
                      <>
                        <span className="original-price">₹{selectedVariant.mrp}</span>
                        {discount > 0 && (
                          <span className="discount-text">{discount}% off</span>
                        )}
                      </>
                    )}
                  </div>
                  {selectedVariant.mrp && selectedVariant.mrp > selectedVariant.price && (
                    <p className="savings-text">You save ₹{selectedVariant.mrp - selectedVariant.price}</p>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="product-quantity-section">
            <label>Quantity:</label>
            <div className="quantity-controls">
              <button 
                onClick={() => handleQuantityChange(-1)} 
                disabled={quantity <= 1}
                className="quantity-btn"
              >
                −
              </button>
              <span className="quantity-value">{quantity}</span>
              <button 
                onClick={() => handleQuantityChange(1)}
                className="quantity-btn"
              >
                +
              </button>
            </div>
          </div>

          <div className="product-actions">
            <button 
              className="add-to-cart-btn"
              onClick={handleAddToCart}
              disabled={adding || (selectedVariant && selectedVariant.stock === 0)}
            >
              {adding ? 'Adding...' : `Add to Cart - ₹${currentPrice * quantity}`}
            </button>
          </div>

          {product.category && (
            <div className="product-meta">
              <div className="meta-item">
                <strong>Category:</strong> {product.category.name || product.category}
              </div>
              {product.subcategory && (
                <div className="meta-item">
                  <strong>Subcategory:</strong> {product.subcategory}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

